import { FC, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Collapse,
    Divider,
    LinearProgress,
    Paper,
    TextField,
    Typography,
    Alert,
    AlertTitle,
    Tooltip,
} from '@mui/material';
import {
    Analytics as AnalyticsIcon,
    CheckCircle,
    Warning,
    Error as ErrorIcon,
    ExpandMore,
    ExpandLess,
    HelpOutline,
    AutoAwesome,
    FileDownload,
    Upgrade,
} from '@mui/icons-material';
import { SCUIButton } from '../../atomic/atoms';
import { SCUIContent } from '../../atomic/organisms';
import {
    AnalyzeTask,
    UpdateTask,
    TaskAnalysisResult,
    TaskAnalysisCategory,
    QuestionAnswer,
} from '../../services/command/taskAnalyzer/TaskAnalyzerCommand.services';
import './TaskAnalyzer.scss';
import { TA, taCls } from './taskAnalyzer.classes';

const getStatusIcon = (status: string) => {
    switch (status) {
        case 'ok':
            return <CheckCircle sx={{ color: '#4caf50' }} />;
        case 'warning':
            return <Warning sx={{ color: '#ff9800' }} />;
        case 'critical':
            return <ErrorIcon sx={{ color: '#f44336' }} />;
        default:
            return <HelpOutline sx={{ color: '#9e9e9e' }} />;
    }
};

const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
    switch (status) {
        case 'ok':
            return 'success';
        case 'warning':
            return 'warning';
        case 'critical':
            return 'error';
        default:
            return 'default';
    }
};

const getScoreColor = (score: number): string => {
    if (score >= 8) return '#4caf50';
    if (score >= 5) return '#ff9800';
    return '#f44336';
};

interface CategoryCardProps {
    category: TaskAnalysisCategory;
    answers: Record<string, string>;
    onAnswerChange: (question: string, answer: string) => void;
}

const CategoryCard: FC<CategoryCardProps> = ({ category, answers, onAnswerChange }) => {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(category.status !== 'ok');
    const hasQuestions = category.questions && category.questions.length > 0;

    return (
        <Card
            className={taCls(TA.categoryCard, `${TA.categoryCard}--${category.status}`)}
            variant="outlined"
        >
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }} className={TA.categoryCardContent}>
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => setExpanded(!expanded)}
                    className={TA.categoryHeader}
                >
                    <Box display="flex" alignItems="center" gap={1.5} className={TA.categoryTitleGroup}>
                        <Box className={TA.categoryStatusIcon}>{getStatusIcon(category.status)}</Box>
                        <Typography variant="subtitle1" fontWeight={600} className={TA.categoryTitle}>
                            {category.name}
                        </Typography>
                        <Chip
                            label={t(`taskAnalyzer.status.${category.status}`)}
                            color={getStatusColor(category.status)}
                            size="small"
                            variant="outlined"
                            className={TA.categoryStatusChip}
                        />
                        {hasQuestions && (
                            <Typography variant="caption" color="text.secondary" className={TA.categoryQuestionCount}>
                                ({category.questions.length} {t('taskAnalyzer.questionCount')})
                            </Typography>
                        )}
                    </Box>
                    <Box className={TA.categoryExpandIcon}>{expanded ? <ExpandLess /> : <ExpandMore />}</Box>
                </Box>

                <Collapse in={expanded}>
                    <Box mt={1.5} className={TA.categoryCollapseBody}>
                        <Typography variant="body2" color="text.secondary" mb={1} className={TA.categoryFeedback}>
                            {category.feedback}
                        </Typography>

                        {hasQuestions && (
                            <Box className={TA.questions}>
                                {category.questions.map((question) => {
                                    const answerValue = answers[question] || '';
                                    const isCritical = category.status === 'critical';
                                    return (
                                        <Box key={question} className={TA.questionItem} mb={1.5}>
                                            <Box display="flex" alignItems="flex-start" gap={1} mb={0.5} className={TA.questionRow}>
                                                <HelpOutline
                                                    className={TA.questionHelpIcon}
                                                    sx={{
                                                        fontSize: 16,
                                                        mt: 0.3,
                                                        color: isCritical ? 'error.main' : 'primary.main',
                                                        flexShrink: 0,
                                                    }}
                                                />
                                                <Typography variant="body2" fontWeight={500} className={TA.questionText}>
                                                    {question}
                                                    {isCritical && (
                                                        <Typography component="span" variant="caption" color="error" ml={0.5}>
                                                            *
                                                        </Typography>
                                                    )}
                                                </Typography>
                                            </Box>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                multiline
                                                minRows={1}
                                                maxRows={4}
                                                value={answerValue}
                                                onChange={(e) => onAnswerChange(question, e.target.value)}
                                                placeholder={t('taskAnalyzer.answerPlaceholder')}
                                                variant="outlined"
                                                className={TA.questionAnswerInput}
                                                sx={{
                                                    ml: 3,
                                                    '& .MuiOutlinedInput-root': { fontSize: '0.85rem' },
                                                }}
                                            />
                                        </Box>
                                    );
                                })}
                            </Box>
                        )}
                    </Box>
                </Collapse>
            </CardContent>
        </Card>
    );
};

const statusEmoji: Record<string, string> = { ok: '\u2705', warning: '\u26a0\ufe0f', critical: '\u274c' };
const statusLabel: Record<string, string> = { ok: 'Yeterli', warning: 'Uyar\u0131', critical: 'Kritik' };

const buildMarkdown = (result: TaskAnalysisResult): string => {
    const sections = [
        '# Talep Analiz Raporu',
        '',
        `**Genel Puan:** ${result.overallScore} / 10`,
        '',
        `**\u00d6zet:** ${result.summary}`,
        '',
        '---',
        '',
    ];

    result.categories?.forEach((cat) => {
        const emoji = statusEmoji[cat.status] || '';
        const label = statusLabel[cat.status] || cat.status;
        sections.push(`## ${emoji} ${cat.name}  \`${label}\``, '');
        if (cat.feedback) {
            sections.push(cat.feedback, '');
        }
        if (cat.questions && cat.questions.length > 0) {
            sections.push('**Sorulmas\u0131 Gereken Sorular:**', '', ...cat.questions.map((q) => `- ${q}`), '');
        }
    });

    return sections.join('\n');
};

const downloadMarkdown = (result: TaskAnalysisResult) => {
    const md = buildMarkdown(result);
    const blob = new Blob([md], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `talep-analiz-${new Date().toISOString().slice(0, 10)}.md`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
};

const AI_SEPARATOR = '\n\n****\n\n> **[AI Tarafından Eklenen Bilgiler]**\n\n';

export const TaskAnalyzer: FC = () => {
    const { t } = useTranslation();
    const [taskDescription, setTaskDescription] = useState('');
    const [loading, setLoading] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [analysisResult, setAnalysisResult] = useState<TaskAnalysisResult | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [answers, setAnswers] = useState<Record<string, string>>({});

    const handleAnswerChange = useCallback((question: string, answer: string) => {
        setAnswers((prev) => ({ ...prev, [question]: answer }));
    }, []);

    const criticalQuestions = useMemo(() => {
        if (!analysisResult) return [];
        return analysisResult.categories
            .filter((c) => c.status === 'critical')
            .flatMap((c) => c.questions || []);
    }, [analysisResult]);

    const allCriticalsAnswered = useMemo(() => {
        if (criticalQuestions.length === 0) return false;
        return criticalQuestions.every((q) => answers[q]?.trim());
    }, [criticalQuestions, answers]);

    const answeredQAs = useMemo((): QuestionAnswer[] => {
        return Object.entries(answers)
            .filter(([, answer]) => answer.trim())
            .map(([question, answer]) => ({ question, answer: answer.trim() }));
    }, [answers]);

    const handleAnalyze = async () => {
        if (!taskDescription.trim()) return;

        setLoading(true);
        setError(null);
        setAnalysisResult(null);
        setAnswers({});

        try {
            const response = await AnalyzeTask({ taskDescription: taskDescription.trim() });
            const parsed: TaskAnalysisResult = JSON.parse(response.analysis);
            if (typeof parsed.overallScore === 'string') {
                parsed.overallScore = Number.parseInt(parsed.overallScore, 10) || 0;
            }
            setAnalysisResult(parsed);
        } catch (err: any) {
            setError(err?.data?.error || err?.message || t('taskAnalyzer.analyzeError'));
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateTask = async () => {
        if (answeredQAs.length === 0) return;

        setUpdating(true);
        setError(null);

        try {
            const response = await UpdateTask({
                originalTask: taskDescription,
                answers: answeredQAs,
            });

            const addition = response.updatedAddition?.trim();
            if (addition) {
                setTaskDescription((prev) => prev + AI_SEPARATOR + addition);
            }

            setAnalysisResult(null);
            setAnswers({});
        } catch (err: any) {
            setError(err?.data?.error || err?.message || t('taskAnalyzer.updateError'));
        } finally {
            setUpdating(false);
        }
    };

    const handleClear = () => {
        setTaskDescription('');
        setAnalysisResult(null);
        setError(null);
        setAnswers({});
    };

    const criticalCount = analysisResult?.categories?.filter((c) => c.status === 'critical').length || 0;
    const warningCount = analysisResult?.categories?.filter((c) => c.status === 'warning').length || 0;
    const okCount = analysisResult?.categories?.filter((c) => c.status === 'ok').length || 0;

    return (
        <SCUIContent headerName={t('taskAnalyzer.title')} className={TA.page}>
            <Box className={TA.root}>
                <Paper className={TA.inputSection} elevation={0} variant="outlined">
                    <Box display="flex" alignItems="center" gap={1} mb={2} className={TA.inputSectionHeader}>
                        <AutoAwesome color="primary" className={TA.inputSectionIcon} />
                        <Typography variant="h6" fontWeight={600} className={TA.inputSectionTitle}>
                            {t('taskAnalyzer.inputTitle')}
                        </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" mb={2} className={TA.inputDescription}>
                        {t('taskAnalyzer.inputDescription')}
                    </Typography>

                    <TextField
                        fullWidth
                        multiline
                        minRows={8}
                        maxRows={20}
                        value={taskDescription}
                        onChange={(e) => setTaskDescription(e.target.value)}
                        placeholder={t('taskAnalyzer.placeholder')}
                        variant="outlined"
                        disabled={loading || updating}
                        className={TA.taskDescriptionInput}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                fontSize: '0.95rem',
                                lineHeight: 1.6,
                            },
                        }}
                    />

                    <Box display="flex" gap={1.5} mt={2} justifyContent="flex-end" className={TA.inputActions}>
                        <SCUIButton
                            type="secondary"
                            sizeType="default"
                            size="m"
                            text={t('taskAnalyzer.clear')}
                            onClick={handleClear}
                            disabled={loading || updating || !taskDescription}
                            className={TA.btnClear}
                        />
                        <SCUIButton
                            type="primary"
                            sizeType="default"
                            size="l"
                            text={loading ? t('taskAnalyzer.analyzing') : t('taskAnalyzer.analyze')}
                            onClick={handleAnalyze}
                            disabled={loading || updating || !taskDescription.trim()}
                            showStartIcon
                            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <AnalyticsIcon />}
                            className={TA.btnAnalyze}
                        />
                    </Box>
                </Paper>

                {(loading || updating) && (
                    <Box mt={3} className={TA.progressSection}>
                        <LinearProgress className={TA.linearProgress} />
                        <Typography variant="body2" color="text.secondary" textAlign="center" mt={1} className={TA.progressMessage}>
                            {updating ? t('taskAnalyzer.updatingMessage') : t('taskAnalyzer.analyzingMessage')}
                        </Typography>
                    </Box>
                )}

                {error && (
                    <Alert severity="error" sx={{ mt: 3 }} className={TA.errorAlert}>
                        <AlertTitle>{t('taskAnalyzer.errorTitle')}</AlertTitle>
                        {error}
                    </Alert>
                )}

                {analysisResult && (
                    <Box className={TA.results} mt={3}>
                        <Paper className={TA.scoreSection} elevation={0} variant="outlined">
                            <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                                flexWrap="wrap"
                                gap={2}
                                className={TA.scoreSectionHeader}
                            >
                                <Box className={TA.resultTitleBlock}>
                                    <Typography variant="h6" fontWeight={600} mb={0.5} className={TA.resultTitle}>
                                        {t('taskAnalyzer.resultTitle')}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" className={TA.resultSummary}>
                                        {analysisResult.summary}
                                    </Typography>
                                </Box>
                                <Box display="flex" alignItems="center" gap={3} className={TA.scoreValueBlock}>
                                    <Box textAlign="center">
                                        <Typography
                                            variant="h3"
                                            fontWeight={700}
                                            sx={{ color: getScoreColor(analysisResult.overallScore) }}
                                            className={TA.scoreValue}
                                        >
                                            {analysisResult.overallScore}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" className={TA.scoreMax}>
                                            / 10
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>

                            <Divider sx={{ my: 2 }} className={TA.scoreDivider} />

                            <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                                flexWrap="wrap"
                                gap={1}
                                className={TA.chipsRow}
                            >
                                <Box display="flex" gap={2} flexWrap="wrap" className={TA.chipsGroup}>
                                    {criticalCount > 0 && (
                                        <Chip
                                            icon={<ErrorIcon />}
                                            label={`${criticalCount} ${t('taskAnalyzer.criticalIssues')}`}
                                            color="error"
                                            variant="outlined"
                                            className={TA.chipCritical}
                                        />
                                    )}
                                    {warningCount > 0 && (
                                        <Chip
                                            icon={<Warning />}
                                            label={`${warningCount} ${t('taskAnalyzer.warnings')}`}
                                            color="warning"
                                            variant="outlined"
                                            className={TA.chipWarning}
                                        />
                                    )}
                                    {okCount > 0 && (
                                        <Chip
                                            icon={<CheckCircle />}
                                            label={`${okCount} ${t('taskAnalyzer.passed')}`}
                                            color="success"
                                            variant="outlined"
                                            className={TA.chipOk}
                                        />
                                    )}
                                </Box>
                                <SCUIButton
                                    type="secondary"
                                    sizeType="default"
                                    size="s"
                                    text={t('taskAnalyzer.exportMd')}
                                    onClick={() => downloadMarkdown(analysisResult)}
                                    showStartIcon
                                    startIcon={<FileDownload />}
                                    className={TA.btnExportMd}
                                />
                            </Box>
                        </Paper>

                        {criticalQuestions.length > 0 && !allCriticalsAnswered && (
                            <Alert severity="info" sx={{ mt: 2 }} className={TA.criticalInfoAlert}>
                                {t('taskAnalyzer.criticalWarning')}
                            </Alert>
                        )}

                        <Box className={TA.categories} mt={2}>
                            {analysisResult.categories?.map((category) => (
                                <CategoryCard
                                    key={category.name}
                                    category={category}
                                    answers={answers}
                                    onAnswerChange={handleAnswerChange}
                                />
                            ))}
                        </Box>

                        <Box mt={3} display="flex" justifyContent="center" className={TA.updateSection}>
                            <Tooltip
                                title={
                                    allCriticalsAnswered
                                        ? t('taskAnalyzer.updateTooltipEnabled')
                                        : t('taskAnalyzer.updateTooltipDisabled')
                                }
                            >
                                <span>
                                    <SCUIButton
                                        type="primary"
                                        sizeType="fixed"
                                        size="l"
                                        text={updating ? t('taskAnalyzer.updating') : t('taskAnalyzer.updateTask')}
                                        onClick={handleUpdateTask}
                                        disabled={!allCriticalsAnswered || updating || loading}
                                        showStartIcon
                                        startIcon={
                                            updating ? <CircularProgress size={20} color="inherit" /> : <Upgrade />
                                        }
                                        className={TA.updateTaskButton}
                                    />
                                </span>
                            </Tooltip>
                        </Box>
                    </Box>
                )}
            </Box>
        </SCUIContent>
    );
};

export default TaskAnalyzer;
